import React, { memo, useMemo, useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Card from '../common/Card';
import Table from '../common/Table';
import { fetchAssetHistory, fetchAssetRequests, setSelectedTab, _cancelAssetRequest, LOANED, RETURNED, REQUESTS } from './../../redux/assets/actions';
import moment from 'moment';
import TabPanel from '../common/TabPanel';
import Loading from '../common/Loading';
import Tabs from '../common/Tabs';
import Tab from '../common/Tab';
import { stopLoadingSelector } from '../../redux/helperSelectors';
import ConfirmModal from '../common/modals/ConfirmModal';
import Modal from '../common/Modal';
import { showModal } from '../../redux/system/actions';
import { getPublicAssetCategories } from '../../api/assetsApi';
import AssetRequestDecisionModal from './AssetRequestDecisionModal';
import { toast } from 'react-toastify';
import { CANCEL_ASSET_REQUEST_TOAST_MESSAGES } from '../../service/toastService';
import '../../assets/css/allAssetsCard.css';

const CONFIRM_CANCEL_REQUEST_MODAL = 'confirmCancelRequestModal';
const VIEW_DECISION_MODAL = 'viewDecisionModal';
const VIEW_MOTIVATION_MODAL = 'viewMotivationModal';

function AssetHistoryCard() {
  const dispatch = useDispatch();
  const { selectedUser, nameToDisplay } = useSelector((state) => state.users);
  const { isFetchingAssetHistory, triedFetchingAssetHistory } = useSelector((state) => state.system);
  const {
    loanedAssetHistory,
    skipLoaned,
    takeLoaned,
    totalCountLoaned,
    loanedAssetHistoryLoaded,
    returnedAssetHistory,
    skipReturned,
    takeReturned,
    totalCountReturned,
    returnedAssetHistoryLoaded,
    assetRequests,
    assetRequestsLoaded,
    selectedTab,
    skipRequests,
    takeRequests,
    totalCountRequests,
  } = useSelector((state) => state.assets);
  const stopLoading = useSelector((state) => stopLoadingSelector(state));
  const isLoading = (isFetchingAssetHistory || !triedFetchingAssetHistory || !(loanedAssetHistoryLoaded && returnedAssetHistoryLoaded)) && !stopLoading;
  const [requestToCancel, setRequestToCancel] = useState(null);
  const [requestFilters, setRequestFilters] = useState({
    status: '',
    assetCategoryId: '',
  });
  const [assetCategories, setAssetCategories] = useState([]);
  const [decisionDetails, setDecisionDetails] = useState(null);
  const [motivationDetails, setMotivationDetails] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await getPublicAssetCategories();
        setAssetCategories(response.data);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    fetchCategories();
  }, []);

  const handleShowModal = useCallback((modalId) => dispatch(showModal(modalId)), [dispatch]);

  const handleChangeTab = useCallback(
    (selectedTab) => {
      dispatch(setSelectedTab(selectedTab));
    },
    [dispatch],
  );

  const handleChangePage = (pageIndex, historyType) => {
    dispatch(fetchAssetHistory(selectedUser.myportalUserId, pageIndex, historyType == LOANED ? takeLoaned : takeReturned, historyType));
  };

  const handleChangeRowsPerPage = (event, historyType) => {
    dispatch(fetchAssetHistory(selectedUser.myportalUserId, 0, event.target.value, historyType));
  };

  const handleChangeRequestsPage = (pageIndex) => {
    dispatch(fetchAssetRequests(selectedUser.myportalUserId, pageIndex, takeRequests, requestFilters));
  };

  const handleChangeRequestsRowsPerPage = (event) => {
    dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, event.target.value, requestFilters));
  };

  const handleRequestFilterChange = useCallback(
    (filterType, value) => {
      const newFilters = { ...requestFilters, [filterType]: value };
      setRequestFilters(newFilters);
      dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, takeRequests, newFilters));
    },
    [dispatch, selectedUser.myportalUserId, requestFilters, takeRequests],
  );

  const clearRequestFilters = useCallback(() => {
    const clearedFilters = {
      status: '',
      assetCategoryId: '',
    };
    setRequestFilters(clearedFilters);
    dispatch(fetchAssetRequests(selectedUser.myportalUserId, 0, takeRequests, clearedFilters));
  }, [dispatch, selectedUser.myportalUserId, takeRequests]);

  const handleConfirmCancel = useCallback(async () => {
    if (requestToCancel) {
      try {
        await dispatch(_cancelAssetRequest(selectedUser.myportalUserId, requestToCancel, skipRequests, takeRequests, requestFilters));
        toast.success('Request cancelled successfully!', CANCEL_ASSET_REQUEST_TOAST_MESSAGES.success);
        setRequestToCancel(null);
      } catch (error) {
        toast.error('Failed to cancel request.', CANCEL_ASSET_REQUEST_TOAST_MESSAGES.error);
      }
    }
  }, [dispatch, selectedUser.myportalUserId, requestToCancel, skipRequests, takeRequests, requestFilters]);

  const handleCancelClick = useCallback(
    (assetRequestId) => {
      setRequestToCancel(assetRequestId);
      handleShowModal(CONFIRM_CANCEL_REQUEST_MODAL);
    },
    [handleShowModal],
  );

  const handleViewCommentClick = useCallback(
    (decision) => {
      setDecisionDetails(decision || null);
      handleShowModal(VIEW_DECISION_MODAL);
    },
    [handleShowModal],
  );

  const handleDecisionModalClose = useCallback(() => {
    setDecisionDetails(null);
  }, []);

  const handleViewMotivationClick = useCallback(
    (motivation) => {
      setMotivationDetails(motivation || 'No motivation provided.');
      handleShowModal(VIEW_MOTIVATION_MODAL);
    },
    [handleShowModal],
  );

  const getRequestStatusClass = (status) => {
    switch (status) {
      case 0:
        return 'badge-warning';
      case 1:
        return 'badge-success';
      case 2:
        return 'badge-danger';
      case 3:
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  };

  const getRequestStatusDisplayName = (status) => {
    switch (status) {
      case 0:
        return 'Pending';
      case 1:
        return 'Approved';
      case 2:
        return 'Rejected';
      case 3:
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };

  const columns = (
    <>
      <th className="col-lg-6">Asset</th>
      <th>Asset Category</th>
      <th>Asset of Tenant</th>
      <th>Loan Date</th>
      <th>Return Date</th>
    </>
  );

  const requestColumns = (
    <>
      <th className="col-lg-6">Asset</th>
      <th>Status</th>
      <th>Asset Category</th>
      <th>Request Date</th>
      <th>Loan Date</th>
      <th>In Inventory</th>
      <th>Actions</th>
    </>
  );

  const mappedLoanedAssetHistory = useMemo(
    () =>
      loanedAssetHistory.map((ah, i) => {
        return (
          <tr key={i}>
            <td>{ah.assetName}</td>
            <td>{ah.assetCategoryName}</td>
            <td>{ah.assetTenantName}</td>
            <td>{ah.loanDate && moment(ah.loanDate).format('D MMMM YYYY')}</td>
            <td>{ah.returnDate && moment(ah.returnDate).format('D MMMM YYYY')}</td>
          </tr>
        );
      }),
    [loanedAssetHistory],
  );

  const mappedReturnedAssetHistory = useMemo(
    () =>
      returnedAssetHistory.map((ah, i) => {
        return (
          <tr key={i}>
            <td>{ah.assetName}</td>
            <td>{ah.assetCategoryName}</td>
            <td>{ah.assetTenantName}</td>
            <td>{ah.loanDate && moment(ah.loanDate).format('D MMMM YYYY')}</td>
            <td>{ah.returnDate && moment(ah.returnDate).format('D MMMM YYYY')}</td>
          </tr>
        );
      }),
    [returnedAssetHistory],
  );

  const mappedAssetRequests = useMemo(() => {
    if (!assetRequests || !Array.isArray(assetRequests)) {
      return [];
    }

    return assetRequests.map((request, i) => {
      let assetName = 'N/A';
      if (request.inInventory && request.asset && request.asset.name) {
        assetName = request.asset.name;
      } else if (!request.inInventory && request.assetName) {
        assetName = request.assetName;
      }

      let categoryName = 'N/A';
      if (request.inInventory && request.asset && request.asset.assetCategory && request.asset.assetCategory.name) {
        categoryName = request.asset.assetCategory.name;
      } else if (!request.inInventory && request.assetCategory && request.assetCategory.name) {
        categoryName = request.assetCategory.name;
      }

      const requestDate = request.requestDate ? moment(request.requestDate).format('D MMMM YYYY') : 'N/A';
      const loanDate = request.loanDate ? moment(request.loanDate).format('D MMMM YYYY') : 'N/A';

      return (
        <tr key={i} className="clickable" style={{ cursor: 'pointer' }} onClick={() => handleViewMotivationClick(request.motivation)}>
          <td>{assetName}</td>
          <td>
            <span className={`badge ${getRequestStatusClass(request.status)}`}>{getRequestStatusDisplayName(request.status)}</span>
          </td>
          <td>{categoryName}</td>
          <td>{requestDate}</td>
          <td>{loanDate}</td>
          <td>{request.inInventory ? <i className="fas fa-check text-success"></i> : <i className="fas fa-times text-danger"></i>}</td>
          <td>
            {request.status === 0 && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancelClick(request.assetRequestId);
                }}>
                Cancel
              </button>
            )}
            {(request.status === 1 || request.status === 2) && (
              <button
                className="btn btn-sm btn-outline-decision"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewCommentClick(request.assetRequestDecision);
                }}
                disabled={!request.assetRequestDecision}>
                View decision
              </button>
            )}
          </td>
        </tr>
      );
    });
  }, [assetRequests, handleCancelClick, handleViewCommentClick]);

  return (
    <>
      <Card title={`${nameToDisplay} Assets`} icon="pe-7s-car" iconGradient="bg-green-blue-top" contentClass={isLoading ? 'no-current-card' : 'card-table-content'}>
        {isLoading ? (
          <Loading message="Hang on, we're loading your asset history." />
        ) : (
          <>
            <Tabs value={selectedTab} onChange={(_, tabValue) => handleChangeTab(tabValue)} TabIndicatorProps={{ style: { background: 'var(--accent-color)' } }}>
              <Tab label="Loaned Assets" />
              <Tab label="Returned Assets" />
              <Tab label="My Requests" />
            </Tabs>
            <TabPanel value={selectedTab} index={0}>
              {mappedLoanedAssetHistory.length > 0 ? (
                <>
                  <Table
                    columns={columns}
                    data={mappedLoanedAssetHistory}
                    totalCount={totalCountLoaned}
                    skip={skipLoaned}
                    take={takeLoaned}
                    changePageHandler={(_, pageIndex) => handleChangePage(pageIndex, LOANED)}
                    changeRowsPerPageHandler={(event) => handleChangeRowsPerPage(event, LOANED)}
                  />
                </>
              ) : (
                <div className="mt-3">You have no loaned assets yet.</div>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              {mappedReturnedAssetHistory.length > 0 ? (
                <>
                  <Table
                    columns={columns}
                    data={mappedReturnedAssetHistory}
                    totalCount={totalCountReturned}
                    skip={skipReturned}
                    changePageHandler={(_, pageIndex) => handleChangePage(pageIndex, RETURNED)}
                    take={takeReturned}
                    changeRowsPerPageHandler={(event) => handleChangeRowsPerPage(event, RETURNED)}
                  />
                </>
              ) : (
                <div className="mt-3">You have no returned assets yet.</div>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={2}>
              <div className="filters-box">
                <div className="row g-1 filters-row justify-content-start">
                  <div className="col-auto">
                    <label className="form-label-sm">Category</label>
                    <select
                      className="form-control filter-control"
                      value={requestFilters.assetCategoryId}
                      onChange={(e) => handleRequestFilterChange('assetCategoryId', e.target.value)}
                      disabled={!assetRequestsLoaded}>
                      <option value="">All Categories</option>
                      {assetCategories.map((category) => (
                        <option key={category.assetCategoryId} value={category.assetCategoryId}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-auto">
                    <label className="form-label-sm">Status</label>
                    <select
                      className="form-control filter-control"
                      value={requestFilters.status}
                      onChange={(e) => handleRequestFilterChange('status', e.target.value)}
                      disabled={!assetRequestsLoaded}>
                      <option value="">All</option>
                      <option value="0">Pending</option>
                      <option value="1">Approved</option>
                      <option value="2">Rejected</option>
                      <option value="3">Cancelled</option>
                    </select>
                  </div>

                  <div className="col-auto flex-grow-1"></div>

                  <div className="col-auto d-flex align-items-end justify-content-end pe-0">
                    <button type="button" className="btn btn-outline-secondary btn-sm clear-btn" onClick={clearRequestFilters} disabled={!assetRequestsLoaded}>
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div className="filters-bottom-spacer" />
              {!assetRequestsLoaded ? (
                <div style={{ marginTop: '10rem' }}>
                  <Loading message="Hang on, we're loading your asset requests." />
                </div>
              ) : mappedAssetRequests.length > 0 ? (
                <>
                  <Table
                    columns={requestColumns}
                    data={mappedAssetRequests}
                    totalCount={totalCountRequests}
                    skip={skipRequests}
                    take={takeRequests}
                    changePageHandler={(_, pageIndex) => handleChangeRequestsPage(pageIndex)}
                    changeRowsPerPageHandler={(event) => handleChangeRequestsRowsPerPage(event)}
                  />
                </>
              ) : (
                <div className="mt-3">
                  {Object.values(requestFilters).some((f) => f !== '') ? 'No requests match your current filters.' : 'You have no asset requests yet.'}
                </div>
              )}
            </TabPanel>
          </>
        )}
      </Card>
      <ConfirmModal
        id={CONFIRM_CANCEL_REQUEST_MODAL}
        title="Cancel Asset Request"
        message="Are you sure you want to cancel this request? This action cannot be undone."
        onConfirm={handleConfirmCancel}
      />
      <Modal
        id={VIEW_MOTIVATION_MODAL}
        backdrop
        title="Request Motivation"
        confirmText="Close"
        cancelText="Close"
        hideCancelButton
        confirmHandler={() => {}}
        contentClass="">
        <div>
          <p style={{ whiteSpace: 'pre-wrap', textAlign: 'left', marginBottom: 0 }}>{motivationDetails || 'No motivation provided.'}</p>
        </div>
      </Modal>
      <AssetRequestDecisionModal id={VIEW_DECISION_MODAL} decision={decisionDetails} onClose={handleDecisionModalClose} />
    </>
  );
}

export default memo(AssetHistoryCard);
